import React from "react";
import { GroupChevron } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import type { DriverShiftGroup, TruckShiftGroup } from "../helpers/shiftGroups";
import { ShiftPlate } from "../ui/shift-plate";

/** The row both views' group headers share: chevron, title, shift count, then grey labels. */
const GroupHeaderContent: React.FC<{
  isExpanded: boolean;
  title: React.ReactNode;
  count: number;
  /** Empty labels are skipped. */
  meta: (string | undefined)[];
}> = ({ isExpanded, title, count, meta }) => (
  <div className="flex items-center gap-4 px-4 h-full whitespace-nowrap">
    <GroupChevron isExpanded={isExpanded} />

    {title}

    <span className="inline-flex min-w-8 items-center justify-center rounded-2xl bg-brand-blue px-2 py-1 text-xs font-bold leading-[14px] text-white">
      {count}
    </span>

    {meta.filter(Boolean).map((label, index) => (
      <span
        key={`${index}-${label}`}
        className="text-sm font-medium leading-4 tracking-[-0.2px] text-icon-secondary"
      >
        {label}
      </span>
    ))}
  </div>
);

/**
 * Contents of a truck's group header row: plate, how many shifts it holds, and
 * the vehicle's model, type and division.
 */
export const TruckShiftsGroupHeader: React.FC<{
  group: TruckShiftGroup;
  isExpanded: boolean;
  /** Shows the truck on the dashboard map. Without it the plate stays plain text. */
  onPlateClick?: (truckId: bigint) => void;
}> = ({ group, isExpanded, onPlateClick }) => (
  <GroupHeaderContent
    isExpanded={isExpanded}
    title={
      <ShiftPlate plateNumber={group.plateNumber} truckId={group.truckId} onOpen={onPlateClick} />
    }
    count={group.totalCount}
    meta={[group.model, group.typeLabel, group.division]}
  />
);

/** Contents of a driver's group header row: name, how many shifts they hold, division and licence. */
export const DriverShiftsGroupHeader: React.FC<{
  group: DriverShiftGroup;
  isExpanded: boolean;
}> = ({ group, isExpanded }) => {
  const t = useT();
  return (
    <GroupHeaderContent
      isExpanded={isExpanded}
      title={
        <span className="text-sm font-semibold text-text-body">
          {group.driverId != null
            ? group.driverName
            : t(Namespace.Autopark, "shifts.group.noDriver")}
        </span>
      }
      count={group.totalCount}
      meta={[group.details.division, group.details.licenseNumber]}
    />
  );
};
